import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getEmployeeMasters } from "../../services/employeeService";
import {
  activateEmployeeFromOnboarding,
  getOnboardingTasksByEmployee,
  initializeOnboardingChecklist,
  updateOnboardingTask
} from "../../services/onboardingService";
import { displayValue } from "../../utils/displayValue";

import "../../styles/hrPageShell.css";
const PROFILE_VERIFICATION_STATUS = {
  VERIFIED: "Verified"
};

const ONBOARDING_STAGE = {
  ONBOARDING: "Onboarding",
  ACTIVE: "Active"
};

const TASK_STATUSES = ["Not Started", "Pending", "In Progress", "Completed", "Blocked", "Not Applicable"];
const TASK_OWNERS = ["HR", "IT", "Manager", "Employee", "Admin"];

function Onboarding() {
  const accounts = [];
  const currentUserEmail = accounts?.[0]?.username || "";

  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [searchText, setSearchText] = useState("");
  const [stageFilter, setStageFilter] = useState("Onboarding");
  const [taskStatusFilter, setTaskStatusFilter] = useState("");

  const getValue = useCallback((record, ...keys) => {
    if (!record) return "";

    for (const key of keys) {
      const value = record[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        if (typeof value === "object") {
          return value.Value || value.value || value.label || value.Label || "";
        }
        return value;
      }
    }

    const recordKeys = Object.keys(record);
    for (const key of keys) {
      const foundKey = recordKeys.find((recordKey) => recordKey.toLowerCase() === String(key).toLowerCase());
      if (foundKey) {
        const value = record[foundKey];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          if (typeof value === "object") {
            return value.Value || value.value || value.label || value.Label || "";
          }
          return value;
        }
      }
    }

    return "";
  }, []);

  const getDisplay = useCallback((record, ...keys) => displayValue(getValue(record, ...keys)), [getValue]);

  const getEmployeeId = useCallback((employee) => getDisplay(employee, "employeeId", "EmployeeId", "EmpId"), [getDisplay]);

  const getWorkEmail = useCallback((employee) => getDisplay(employee, "workEmail", "WorkEmail"), [getDisplay]);

  const getEmployeeName = useCallback((employee) => {
    const fullName = getDisplay(employee, "fullName", "FullName", "Title");
    if (fullName && fullName !== "-") return fullName;

    return [
      getDisplay(employee, "firstName", "FirstName"),
      getDisplay(employee, "lastName", "LastName")
    ].filter((value) => value && value !== "-").join(" ");
  }, [getDisplay]);

  const getEmployeePrimaryText = useCallback(
    (employee) => getEmployeeName(employee) || getEmployeeId(employee) || "-",
    [getEmployeeName, getEmployeeId]
  );

  const getTaskOnboardingId = useCallback(
    (task) => getDisplay(task, "onboardingId", "OnboardingId"),
    [getDisplay]
  );

  const getTaskRowKey = useCallback(
    (task, index) => getTaskOnboardingId(task) || getDisplay(task, "id", "ID") || String(index),
    [getTaskOnboardingId, getDisplay]
  );

  
  const formatDateForInput = useCallback((value) => {
    const text = displayValue(value);
    if (!text || text === "-") return "";

    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().slice(0, 10);
    }

    return text.length >= 10 ? text.slice(0, 10) : text;
  }, []);
const loadEmployees = useCallback(async () => {
    try {
      setLoadingEmployees(true);
      setMessage("");

      const rows = await getEmployeeMasters();
      setEmployees(rows || []);
    } catch (error) {
      console.error("Onboarding employees load error:", error);
      setMessage(error.message || "Failed to load onboarding employees.");
      setMessageType("error");
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  const loadTasksForEmployee = async (employee) => {
    if (!employee) return;

    try {
      setLoadingTasks(true);
      setMessage("");

      const rows = await getOnboardingTasksByEmployee({
        employeeId: getEmployeeId(employee),
        workEmail: getWorkEmail(employee)
      });

      setTasks(rows || []);
    } catch (error) {
      console.error("Onboarding tasks load error:", error);
      setMessage(error.message || "Failed to load onboarding checklist.");
      setMessageType("error");
      setTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const onboardingEmployees = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return employees.filter((employee) => {
      const verificationStatus = getDisplay(employee, "profileVerificationStatus", "ProfileVerificationStatus");
      const onboardingStage = getDisplay(employee, "onboardingStage", "OnboardingStage");

      const isEligible =
        verificationStatus === PROFILE_VERIFICATION_STATUS.VERIFIED ||
        onboardingStage === ONBOARDING_STAGE.ONBOARDING ||
        onboardingStage === ONBOARDING_STAGE.ACTIVE;

      const matchesStage = !stageFilter || onboardingStage === stageFilter;

      const matchesSearch =
        !query ||
        getEmployeeId(employee).toLowerCase().includes(query) ||
        getWorkEmail(employee).toLowerCase().includes(query) ||
        getEmployeePrimaryText(employee).toLowerCase().includes(query) ||
        getDisplay(employee, "department", "Department").toLowerCase().includes(query) ||
        getDisplay(employee, "designation", "Designation").toLowerCase().includes(query);

      return isEligible && matchesStage && matchesSearch;
    });
  }, [employees, searchText, stageFilter, getDisplay, getEmployeeId, getWorkEmail, getEmployeePrimaryText]);

  const filteredTasks = useMemo(() => {
    if (!taskStatusFilter) return tasks;
    return tasks.filter((task) => getDisplay(task, "taskStatus", "TaskStatus") === taskStatusFilter);
  }, [tasks, taskStatusFilter, getDisplay]);

  const taskSummary = useMemo(() => {
    const total = tasks.length;
    const completed = tasks.filter((task) => ["Completed", "Not Applicable"].includes(getDisplay(task, "taskStatus", "TaskStatus"))).length;
    const blocked = tasks.filter((task) => getDisplay(task, "taskStatus", "TaskStatus") === "Blocked").length;
    const pending = total - completed;
    const percentage = total === 0 ? 0 : Math.round((completed / total) * 100);

    return { total, completed, pending, blocked, percentage };
  }, [tasks, getDisplay]);

    const selectedOnboardingStage = getDisplay(selectedEmployee, "onboardingStage", "OnboardingStage");
    const selectedEmploymentStatus = getDisplay(selectedEmployee, "employmentStatus", "EmploymentStatus");

    const isSelectedEmployeeActive =
    selectedOnboardingStage === ONBOARDING_STAGE.ACTIVE ||
    selectedEmploymentStatus === ONBOARDING_STAGE.ACTIVE;

    const canInitializeChecklist =
    selectedEmployee &&
    !isSelectedEmployeeActive &&
    taskSummary.total === 0;

    const canActivateEmployee =
    selectedEmployee &&
    !isSelectedEmployeeActive &&
    taskSummary.total > 0 &&
    taskSummary.pending === 0;


  const selectEmployee = async (employee) => {
    setSelectedEmployee(employee);
    setTaskStatusFilter("");
    await loadTasksForEmployee(employee);
  };

  const handleInitializeChecklist = async () => {
  if (!selectedEmployee || !canInitializeChecklist) return;

    try {
      setSaving(true);
      setMessage("");

      await initializeOnboardingChecklist({
        employeeId: getEmployeeId(selectedEmployee),
        workEmail: getWorkEmail(selectedEmployee),
        employeeName: getEmployeePrimaryText(selectedEmployee),
        initializedBy: currentUserEmail
      });

      setMessage("Onboarding checklist initialized successfully.");
      setMessageType("success");
      await loadTasksForEmployee(selectedEmployee);
      await loadEmployees();
    } catch (error) {
      console.error("Initialize onboarding checklist error:", error);
      setMessage(error.message || "Failed to initialize onboarding checklist.");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };

  const handleTaskChange = (taskKey, fieldName, value) => {
    setTasks((prev) => prev.map((task, index) => {
      const key = getTaskRowKey(task, index);
      if (key !== taskKey) return task;
      return { ...task, [fieldName]: value };
    }));
  };

  const handleSaveTask = async (task, index) => {
    try {
      setSaving(true);
      setMessage("");

      const onboardingId = getTaskOnboardingId(task);
      if (!onboardingId) {
        throw new Error("Checklist task is missing onboardingId. Please confirm HR_Onboarding_Get_By_Employee returns onboardingId from SharePoint.");
      }

      await updateOnboardingTask({
        onboardingId,
        taskName: getDisplay(task, "taskName", "TaskName"),
        taskStatus: getDisplay(task, "taskStatus", "TaskStatus") || "Pending",
        taskOwner: getDisplay(task, "taskOwner", "TaskOwner") || "HR",
        dueDate: getDisplay(task, "dueDate", "DueDate") || null,
        comments: getDisplay(task, "comments", "Comments") || "",
        completedBy: currentUserEmail || "system"
      });

      setMessage("Onboarding task updated successfully.");
      setMessageType("success");
      await loadTasksForEmployee(selectedEmployee);
    } catch (error) {
      console.error("Update onboarding task error:", error);
      setMessage(error.message || "Failed to update onboarding task. Check HR_Onboarding_Update_Task flow response and required SharePoint field mapping.");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };

  const handleActivateEmployee = async () => {
  if (!selectedEmployee || !canActivateEmployee) return;

    try {
      setSaving(true);
      setMessage("");

      await activateEmployeeFromOnboarding({
        employeeId: getEmployeeId(selectedEmployee),
        workEmail: getWorkEmail(selectedEmployee),
        activatedBy: currentUserEmail,
        comments: "All onboarding checklist tasks completed. Employee activated."
      });

      setMessage("Employee activated successfully.");
        setMessageType("success");

        const activatedEmployee = {
        ...selectedEmployee,
        onboardingStage: ONBOARDING_STAGE.ACTIVE,
        employmentStatus: ONBOARDING_STAGE.ACTIVE
        };

        setSelectedEmployee(activatedEmployee);

        setEmployees((prev) =>
        prev.map((employee) =>
            getEmployeeId(employee) === getEmployeeId(selectedEmployee)
            ? activatedEmployee
            : employee
        )
        );

        await loadEmployees();
        await loadTasksForEmployee(activatedEmployee);
    } catch (error) {
      console.error("Activate employee error:", error);
      setMessage(error.message || "Failed to activate employee.");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };

  const getTaskBadgeStyle = (status) => {
    if (["Completed", "Not Applicable"].includes(status)) {
      return { ...badgeStyle, color: "#166534", backgroundColor: "#dcfce7", borderColor: "#bbf7d0" };
    }
    if (status === "Blocked") {
      return { ...badgeStyle, color: "#991b1b", backgroundColor: "#fee2e2", borderColor: "#fecaca" };
    }
    if (["Pending", "In Progress"].includes(status)) {
      return { ...badgeStyle, color: "#92400e", backgroundColor: "#fef3c7", borderColor: "#fde68a" };
    }
    return { ...badgeStyle, color: "var(--fm-page-accent)", backgroundColor: "#e0f2fe", borderColor: "#bae6fd" };
  };

  return (
    <div className="fm-hr-page-shell" style={pageStyle}>
      <div className="fm-hr-hero" style={headerStyle}>
        <div>
          <h1 style={{ margin: 0 }}>Onboarding</h1>
          <p style={{ marginTop: "8px", color: "rgba(255,255,255,0.88)" }}>
            Manage verified employees from onboarding checklist to active employment.
          </p>
        </div>
        <button type="button" style={refreshButtonStyle} onClick={loadEmployees} disabled={loadingEmployees}>
          {loadingEmployees ? "Loading..." : "Refresh"}
        </button>
      </div>

      {message && <div style={messageType === "error" ? errorMessageStyle : successMessageStyle}>{message}</div>}

      <div style={kpiGridStyle}>
        <div style={kpiCardStyle}>
          <div style={kpiLabelStyle}>Onboarding Employees</div>
          <div style={kpiValueStyle}>{onboardingEmployees.length}</div>
        </div>
        <div style={kpiCardStyle}>
          <div style={kpiLabelStyle}>Selected Checklist Completion</div>
          <div style={kpiValueStyle}>{taskSummary.percentage}%</div>
        </div>
        <div style={kpiCardStyle}>
          <div style={kpiLabelStyle}>Pending Tasks</div>
          <div style={kpiValueStyle}>{taskSummary.pending}</div>
        </div>
        <div style={kpiCardStyle}>
          <div style={kpiLabelStyle}>Blocked Tasks</div>
          <div style={kpiValueStyle}>{taskSummary.blocked}</div>
        </div>
      </div>

      <div style={layoutStyle}>
        <div style={leftPanelStyle}>
          <div style={cardStyle}>
            <h2 style={{ marginTop: 0 }}>Employees</h2>
            <div style={filterGridStyle}>
              <input
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search employee ID, email, department..."
                style={inputStyle}
              />
              <select value={stageFilter} onChange={(event) => setStageFilter(event.target.value)} style={inputStyle}>
                <option value="">All Stages</option>
                <option value="Onboarding">Onboarding</option>
                <option value="Active">Active</option>
              </select>
            </div>

            <div style={employeeListStyle}>
              {onboardingEmployees.length === 0 && (
                <div style={emptyBoxStyle}>{loadingEmployees ? "Loading employees..." : "No onboarding employees found."}</div>
              )}

              {onboardingEmployees.map((employee) => {
                const employeeId = getEmployeeId(employee);
                const isSelected = getEmployeeId(selectedEmployee) === employeeId;

                return (
                  <button
                    key={employeeId || getWorkEmail(employee)}
                    type="button"
                    style={isSelected ? selectedEmployeeCardStyle : employeeCardStyle}
                    onClick={() => selectEmployee(employee)}
                  >
                    <strong>{getEmployeePrimaryText(employee)}</strong>
                    <div style={smallTextStyle}>{getWorkEmail(employee) || "-"}</div>
                    <div style={miniMetaStyle}>
                      {getDisplay(employee, "department", "Department") || "-"} • {getDisplay(employee, "designation", "Designation") || "-"}
                    </div>
                    <div style={{ marginTop: "8px" }}>
                      <span style={getTaskBadgeStyle(getDisplay(employee, "onboardingStage", "OnboardingStage") || "Onboarding")}>
                        {getDisplay(employee, "onboardingStage", "OnboardingStage") || "Onboarding"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div style={rightPanelStyle}>
          <div style={cardStyle}>
            {!selectedEmployee && (
              <div style={emptyBoxStyle}>Select an employee to view or initialize onboarding checklist.</div>
            )}

            {selectedEmployee && (
              <>
                <div style={selectedHeaderStyle}>
                  <div>
                    <h2 style={{ margin: 0 }}>{getEmployeePrimaryText(selectedEmployee)}</h2>
                    <p style={{ marginTop: "6px", color: "rgba(255,255,255,0.88)" }}>
                      {getEmployeeId(selectedEmployee)} • {getWorkEmail(selectedEmployee)}
                    </p>
                  </div>
                  <div style={buttonGroupStyle}>
                    <button type="button" style={secondaryButtonStyle} onClick={() => loadTasksForEmployee(selectedEmployee)} disabled={loadingTasks}>
                      {loadingTasks ? "Loading..." : "Reload Tasks"}
                    </button>
                    <button
                        type="button"
                        style={isSelectedEmployeeActive ? disabledButtonStyle : primaryButtonStyle}
                        onClick={handleInitializeChecklist}
                        disabled={!canInitializeChecklist || saving}
                        >
                        {isSelectedEmployeeActive
                            ? "Checklist Locked"
                            : taskSummary.total > 0
                            ? "Checklist Initialized"
                            : "Initialize Checklist"}
                     </button>
                     <button
                        type="button"
                        style={isSelectedEmployeeActive ? disabledButtonStyle : activateButtonStyle}
                        onClick={handleActivateEmployee}
                        disabled={!canActivateEmployee || saving}
                        >
                        {isSelectedEmployeeActive ? "Employee Active" : "Activate Employee"}
                      </button>
                  </div>
                </div>

                <div style={progressWrapperStyle}>
                  <div style={progressMetaStyle}>
                    <strong>Checklist completion: {taskSummary.percentage}%</strong>
                    <span>{taskSummary.completed} of {taskSummary.total} tasks completed</span>
                  </div>
                  <div style={progressTrackStyle}>
                    <div style={{ ...progressFillStyle, width: `${taskSummary.percentage}%` }} />
                  </div>
                </div>

                <div style={{ marginBottom: "14px" }}>
                  <select value={taskStatusFilter} onChange={(event) => setTaskStatusFilter(event.target.value)} style={inputStyle}>
                    <option value="">All Task Statuses</option>
                    {TASK_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
                  </select>
                </div>

                {filteredTasks.length === 0 && (
                  <div style={emptyBoxStyle}>
                    {loadingTasks ? "Loading checklist..." : "No checklist tasks found. Click Initialize Checklist."}
                  </div>
                )}

                <div style={taskGridStyle}>
                  {filteredTasks.map((task, index) => {
                    const onboardingId = getTaskRowKey(task, index);
                    const taskStatus = getDisplay(task, "taskStatus", "TaskStatus") || "Pending";

                    return (
                      <div key={onboardingId} style={taskCardStyle}>
                        <div style={taskHeaderStyle}>
                          <strong>{getDisplay(task, "taskName", "TaskName") || "Onboarding Task"}</strong>
                          <span style={getTaskBadgeStyle(taskStatus)}>{taskStatus}</span>
                        </div>

                        <div style={taskFormGridStyle}>
                          <label style={labelStyle}>
                            Status
                            <select
                              value={taskStatus}
                              onChange={(event) => handleTaskChange(onboardingId, "taskStatus", event.target.value)}
                              style={inputStyle}
                            >
                              {TASK_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
                            </select>
                          </label>
                          <label style={labelStyle}>
                            Owner
                            <select
                              value={getDisplay(task, "taskOwner", "TaskOwner") || "HR"}
                              onChange={(event) => handleTaskChange(onboardingId, "taskOwner", event.target.value)}
                              style={inputStyle}
                            >
                              {TASK_OWNERS.map((owner) => <option key={owner} value={owner}>{owner}</option>)}
                            </select>
                          </label>
                          <label style={labelStyle}>
                            Due Date
                            <input
                              type="date"
                              value={formatDateForInput(getDisplay(task, "dueDate", "DueDate"))}
                              onChange={(event) => handleTaskChange(onboardingId, "dueDate", event.target.value)}
                              style={inputStyle}
                            />
                          </label>
                        </div>

                        <label style={labelStyle}>
                          Comments
                          <textarea
                            value={getDisplay(task, "comments", "Comments")}
                            onChange={(event) => handleTaskChange(onboardingId, "comments", event.target.value)}
                            style={textareaStyle}
                            rows={3}
                            placeholder="Add task notes or blockers..."
                          />
                        </label>

                        <div style={taskFooterStyle}>
                          <span style={smallTextStyle}>
                            Completed By: {getDisplay(task, "completedBy", "CompletedBy") || "-"}
                          </span>
                          <button type="button" style={primaryButtonStyle} onClick={() => handleSaveTask(task, index)} disabled={saving}>
                            Save Task
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const pageStyle = { fontFamily: "Segoe UI, Calibri, Arial, sans-serif", padding: "18px", backgroundColor: "var(--fm-content)", minHeight: "100vh", color: "var(--fm-text)", boxSizing: "border-box", overflowX: "hidden" };
const headerStyle = { display: "flex", justifyContent: "space-between", gap: "18px", alignItems: "flex-start", flexWrap: "wrap", marginBottom: "16px", background: "var(--fm-hero-bg)", color: "#ffffff", borderRadius: "20px", padding: "22px 24px", boxShadow: "0 10px 28px rgba(0,80,150,0.16)" };
const cardStyle = { backgroundColor: "var(--fm-card)", border: "1px solid var(--fm-border)", borderRadius: "18px", padding: "16px", boxShadow: "0 3px 13px rgba(15,23,42,0.052)", minWidth: 0, overflow: "hidden" };
const kpiGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "11px", marginBottom: "14px", minWidth: 0 };
const kpiCardStyle = { ...cardStyle, padding: "13px" };
const kpiLabelStyle = { color: "var(--fm-muted)", fontSize: "11.5px", fontWeight: 750 };
const kpiValueStyle = { color: "var(--fm-page-accent)", fontSize: "26px", fontWeight: 800, marginTop: "4px", lineHeight: 1.15 };
const layoutStyle = { display: "grid", gridTemplateColumns: "minmax(260px, 360px) minmax(0, 1fr)", gap: "14px", alignItems: "start", minWidth: 0 };
const leftPanelStyle = { minWidth: 0 };
const rightPanelStyle = { minWidth: 0 };
const filterGridStyle = { display: "grid", gridTemplateColumns: "1fr", gap: "10px", marginBottom: "14px", minWidth: 0 };
const inputStyle = { width: "100%", height: "40px", border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "8px 10px", fontSize: "13px", backgroundColor: "var(--fm-card)", boxSizing: "border-box", minWidth: 0 };
const textareaStyle = { width: "100%", border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px", fontSize: "13px", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" };
const employeeListStyle = { display: "grid", gap: "10px", maxHeight: "70vh", overflowY: "auto" };
const employeeCardStyle = { textAlign: "left", backgroundColor: "var(--fm-card)", border: "1px solid var(--fm-border)", borderRadius: "14px", padding: "12px", cursor: "pointer", boxShadow: "0 3px 13px rgba(15,23,42,0.03)", minWidth: 0 };
const selectedEmployeeCardStyle = { ...employeeCardStyle, borderColor: "var(--fm-page-accent)", backgroundColor: "#eef6ff", boxShadow: "0 0 0 2px rgba(0,120,212,0.12)" };
const smallTextStyle = { color: "var(--fm-muted)", fontSize: "12px", marginTop: "3px" };
const miniMetaStyle = { color: "var(--fm-text)", fontSize: "12px", marginTop: "6px" };
const emptyBoxStyle = { padding: "18px", textAlign: "center", color: "var(--fm-muted)", border: "1px dashed #cbd5e1", borderRadius: "14px", backgroundColor: "#f8fafc" };
const selectedHeaderStyle = { display: "flex", justifyContent: "space-between", gap: "16px", alignItems: "flex-start", marginBottom: "16px", flexWrap: "wrap" };
const buttonGroupStyle = { display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "flex-end" };
const primaryButtonStyle = { backgroundColor: "var(--fm-page-accent)", color: "#ffffff", border: "1px solid #0078d4", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const secondaryButtonStyle = { backgroundColor: "#eef6ff", color: "var(--fm-page-accent)", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const activateButtonStyle = { backgroundColor: "#047857", color: "#ffffff", border: "1px solid #047857", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "pointer", fontSize: "12.5px" };
const disabledButtonStyle = { backgroundColor: "#94a3b8", color: "#ffffff", border: "1px solid #94a3b8", borderRadius: "10px", padding: "9px 13px", fontWeight: 750, cursor: "not-allowed", fontSize: "12.5px" };
const refreshButtonStyle = { ...secondaryButtonStyle };
const progressWrapperStyle = { marginBottom: "16px" };
const progressMetaStyle = { display: "flex", justifyContent: "space-between", gap: "10px", color: "var(--fm-text)", fontSize: "12.5px", marginBottom: "8px", flexWrap: "wrap" };
const progressTrackStyle = { height: "12px", backgroundColor: "#e5e7eb", borderRadius: "999px", overflow: "hidden" };
const progressFillStyle = { height: "100%", backgroundColor: "#047857", transition: "width 0.25s ease" };
const taskGridStyle = { display: "grid", gap: "14px" };
const taskCardStyle = { border: "1px solid var(--fm-border)", borderRadius: "14px", padding: "14px", backgroundColor: "var(--fm-card)", minWidth: 0 };
const taskHeaderStyle = { display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", marginBottom: "12px", flexWrap: "wrap" };
const taskFormGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "12px", marginBottom: "12px", minWidth: 0 };
const taskFooterStyle = { display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", marginTop: "12px", flexWrap: "wrap" };
const labelStyle = { display: "grid", gap: "6px", fontWeight: 750, color: "var(--fm-text)", fontSize: "12.5px", minWidth: 0 };
const badgeStyle = { display: "inline-flex", border: "1px solid", borderRadius: "999px", padding: "5px 10px", fontSize: "11.5px", fontWeight: 750, whiteSpace: "nowrap" };
const successMessageStyle = { border: "1px solid #bbf7d0", backgroundColor: "#ecfdf5", color: "#047857", borderRadius: "13px", padding: "11px 13px", marginBottom: "14px", fontWeight: 700 };
const errorMessageStyle = { border: "1px solid #fecaca", backgroundColor: "#fef2f2", color: "#b91c1c", borderRadius: "13px", padding: "11px 13px", marginBottom: "14px", fontWeight: 700 };
export default Onboarding;
